// The Index Sheet Builder tab of the Device Specification Utility. It creates Index Sheets only;
// datasheets are created and assigned in the Project Datasheet Composer.
//
// Guided builds one code from its parts (ISA type, manufacturer, size or series group, the next free
// instance, an optional FL/FO/FC suffix). Suggested grouping proposes one sheet per size group of the
// tags picked. Manual takes a typed code. Codes are global: the next free instance and every
// uniqueness check cover the master catalog and every active project database, and the server checks
// again when the sheet is created.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ListPlus, Wand2, RefreshCw } from 'lucide-react';
import { dsSpecApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import SearchClearIcon from '../../components/common/SearchClearIcon';
import CollapsibleSectionHeader from '../project-management/CollapsibleSectionHeader';
import { applySearch, SEARCH_HINT } from '../../utils/listSearch';
import {
  STATUSES, TYPES, SUFFIXES, defaultType, defaultBasis, normalizeGroup, guidedGroup, guidedPrefix, guidedCode, manualCheck,
  manufacturersWithCode, manufacturerLabel, proposalDescription, createSummary, errText, sizeLabel, sortSizes, prefillFromCatalog,
} from './deviceSpec';
import StuiSelect from '../../components/common/StuiSelect';

const TAG_SEARCH_FIELDS = ['tagId', 'description', 'size', 'indexSheet'];
const EMPTY_FIELDS = { description: '', model: '', specification: '', type: '', status: 'Active' };

// The next free instance for a prefix, re-read whenever the prefix changes (debounced).
function useNextInstance(prefix) {
  const [state, setState] = useState({ prefix: null, next: null, inUse: [], unreadable: 0, loading: false });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!prefix) { setState({ prefix: null, next: null, inUse: [], unreadable: 0, loading: false }); return undefined; }
    let live = true;
    setState(s => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      dsSpecApi.nextInstance(prefix)
        .then(r => { if (live) setState({ prefix, next: r.data.next?.[0] ?? null, inUse: r.data.inUse ?? [], unreadable: r.data.unreadable ?? 0, loading: false }); })
        .catch(() => { if (live) setState({ prefix, next: null, inUse: [], unreadable: 0, loading: false }); });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [prefix, tick]);
  return [state.prefix === prefix ? state : { prefix, next: null, inUse: [], unreadable: 0, loading: true }, () => setTick(n => n + 1)];
}

// Description / Model / Specification / Type / Status, shared by all three modes.
function SheetFields({ fields, setFields, isa, idPrefix, hideDescription = false }) {
  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));
  const type = fields.type || defaultType(isa);
  return (
    <div className="stui-detail-grid">
      {!hideDescription && (
        <div className="stui-col-24 stui-field-group">
          <label htmlFor={`${idPrefix}-desc`}>Description</label>
          <input id={`${idPrefix}-desc`} className="stui-field-input" maxLength={220} value={fields.description} onChange={e => set('description', e.target.value)} />
        </div>
      )}
      <div className="stui-col-8 stui-field-group">
        <label htmlFor={`${idPrefix}-model`}>Model</label>
        <input id={`${idPrefix}-model`} className="stui-field-input" maxLength={220} value={fields.model} onChange={e => set('model', e.target.value)} />
      </div>
      <div className="stui-col-8 stui-field-group">
        <label htmlFor={`${idPrefix}-spec`}>Specification</label>
        <input id={`${idPrefix}-spec`} className="stui-field-input" maxLength={220} value={fields.specification} onChange={e => set('specification', e.target.value)} />
      </div>
      <div className="stui-col-4 stui-field-group">
        <label htmlFor={`${idPrefix}-type`}>Type</label>
        <StuiSelect id={`${idPrefix}-type`} className="stui-field-input" value={type} onChange={e => set('type', e.target.value)}>
          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </StuiSelect>
      </div>
      <div className="stui-col-4 stui-field-group">
        <label htmlFor={`${idPrefix}-status`}>Status</label>
        <StuiSelect id={`${idPrefix}-status`} className="stui-field-input" value={fields.status} onChange={e => set('status', e.target.value)}>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </StuiSelect>
      </div>
    </div>
  );
}

function IsaSelect({ id, label = 'ISA type', isaTypes, value, onChange }) {
  return (
    <div className="stui-field-group">
      <label htmlFor={id}>{label}</label>
      <StuiSelect id={id} className="stui-field-input" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Pick an ISA type…</option>
        {isaTypes.filter(t => t.codeable).map(t => (
          <option key={t.id} value={t.id}>{`${t.isaType} — ${t.description ?? ''}${t.tagCount ? ` (${t.tagCount} tags)` : ''}`}</option>
        ))}
      </StuiSelect>
    </div>
  );
}

function MfrSelect({ id, manufacturers, value, onChange }) {
  return (
    <div className="stui-field-group">
      <label htmlFor={id}>Manufacturer</label>
      <StuiSelect id={id} className="stui-field-input" value={value} onChange={e => onChange(e.target.value)}>
        <option value="">Pick a manufacturer…</option>
        {manufacturers.map(m => <option key={m.id} value={m.id}>{manufacturerLabel(m)}</option>)}
      </StuiSelect>
    </div>
  );
}

function SuffixSelect({ id, value, onChange }) {
  return (
    <div className="stui-field-group">
      <label htmlFor={id}>Suffix</label>
      <StuiSelect id={id} className="stui-field-input" value={value} onChange={e => onChange(e.target.value)}>
        {SUFFIXES.map(s => <option key={s} value={s}>{s || 'None'}</option>)}
      </StuiSelect>
    </div>
  );
}

function CodePreview({ code, note, tone }) {
  return (
    <div className={`stui-inline-notice${tone ? ` stui-inline-notice-${tone}` : ''} stui-mb-md`}>
      <span className="stui-mono">{code || '—'}</span>
      {note ? <span>{` ${note}`}</span> : null}
    </div>
  );
}

export default function IndexSheetBuilderTab({ mode, onModeChange, isaTypes, manufacturers, sizes, prefill, onPrefillUsed, onCreated }) {
  const { notify } = useNotifications();
  const { currentUser } = useAuth();
  const auditCfg = useMemo(() => ({ headers: { 'X-Changed-By': currentUser?.email || '' } }), [currentUser]);
  const [collapsed, setCollapsed] = useState({});
  const toggleSection = id => setCollapsed(c => ({ ...c, [id]: !c[id] }));
  const [busy, setBusy] = useState(false);

  // ─── Guided ──────────────────────────────────────────────────────────────
  const [g, setG] = useState({ isaTypeId: '', manufacturerId: '', basis: 'Series', sizeId: '', manualGroup: '', suffix: '' });
  const [gFields, setGFields] = useState(EMPTY_FIELDS);
  const [gSource, setGSource] = useState(null);
  const gIsa = isaTypes.find(t => String(t.id) === String(g.isaTypeId));
  const gMfr = manufacturers.find(m => String(m.id) === String(g.manufacturerId));
  const sortedSizes = useMemo(() => sortSizes(sizes), [sizes]);
  const gSize = sizes.find(s => String(s.id) === String(g.sizeId));
  const gGroup = guidedGroup({ basis: g.basis, sizeGroup: gSize?.group ?? null, manualGroup: g.manualGroup });
  const gPrefix = guidedPrefix({ isa: gIsa?.isaType, mfrCode: gMfr?.indexCode, group: gGroup });
  const [gNext, refreshNext] = useNextInstance(mode === 'guided' ? gPrefix : null);
  const gBuilt = guidedCode({ isa: gIsa?.isaType, mfrCode: gMfr?.indexCode, group: gGroup, instance: gNext.next, suffix: g.suffix });
  const setGuided = (k, v) => setG(s => ({ ...s, [k]: v }));

  const applyPrefill = useCallback(p => {
    onModeChange?.('guided');
    setG({ isaTypeId: p.isaTypeId, manufacturerId: p.manufacturerId, basis: 'Series', sizeId: '', manualGroup: p.manualGroup, suffix: p.suffix });
    setGFields({ ...EMPTY_FIELDS, description: p.description, model: p.model, specification: p.specification, type: p.type });
    setGSource(p);
  }, [onModeChange]);

  // "Open in Builder" from a spec browser fills the guided form.
  useEffect(() => {
    if (!prefill) return;
    applyPrefill(prefill);
    onPrefillUsed?.();
  }, [prefill, onPrefillUsed, applyPrefill]);

  // Start from an existing sheet: search the catalog and this database by model / specification.
  const [findQ, setFindQ] = useState('');
  const [found, setFound] = useState({ q: '', rows: [], loading: false, error: null });
  useEffect(() => {
    const q = findQ.trim();
    if (q.length < 2) { setFound({ q: '', rows: [], loading: false, error: null }); return undefined; }
    let live = true;
    setFound(f => ({ ...f, loading: true }));
    const t = setTimeout(async () => {
      try {
        const res = await dsSpecApi.search(q);
        if (live) setFound({ q, rows: res.data ?? [], loading: false, error: null });
      } catch (e) {
        if (live) setFound({ q, rows: [], loading: false, error: errText(e, 'Search failed.') });
      }
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [findQ]);
  const startFrom = row => {
    const p = prefillFromCatalog(row, isaTypes, manufacturers);
    if (!p) { notify.warning(`${row.indexSheet} does not follow the code grammar.`); return; }
    applyPrefill(p);
    setFindQ('');
  };

  // ─── Manual ──────────────────────────────────────────────────────────────
  const [mCode, setMCode] = useState('');
  const [mMfrId, setMMfrId] = useState('');
  const [mFields, setMFields] = useState(EMPTY_FIELDS);
  const mCheck = manualCheck(mCode);
  const mMfrPart = mCheck.ok ? mCheck.parts.mfr : null;
  const mMatches = useMemo(() => (mMfrPart ? manufacturersWithCode(manufacturers, mMfrPart) : []), [mMfrPart, manufacturers]);
  const [mNext] = useNextInstance(mode === 'manual' && mCheck.ok ? mCheck.parts.prefix : null);
  const mInUse = mCheck.ok && mNext.inUse.includes(mCode.trim().toUpperCase());
  useEffect(() => {
    if (mMatches.length === 1) setMMfrId(String(mMatches[0].id));
    else if (!mMatches.some(m => String(m.id) === String(mMfrId))) setMMfrId('');
  }, [mMatches, mMfrId]);

  // ─── Suggested grouping ──────────────────────────────────────────────────
  const [s, setS] = useState({ isaTypeId: '', manufacturerId: '', tagIsaTypeId: '', basis: '', seriesGroup: '', unsizedGroup: '', suffix: '' });
  const [sFields, setSFields] = useState(EMPTY_FIELDS);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagSearch, setTagSearch] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [proposal, setProposal] = useState(null);     // server answer + per-row { include, description }
  const [assignTags, setAssignTags] = useState(false);
  const sIsa = isaTypes.find(t => String(t.id) === String(s.isaTypeId));
  const setSuggested = (k, v) => { setS(x => ({ ...x, [k]: v })); setProposal(null); };

  // The tag list follows the code's ISA type unless another is picked for the tags (a TCV tag can
  // carry a CV sheet).
  const tagIsaId = s.tagIsaTypeId || s.isaTypeId;
  useEffect(() => {
    if (mode !== 'suggested' || !tagIsaId) { setTags([]); return; }
    setTagsLoading(true);
    setPicked(new Set());
    dsSpecApi.getTags(tagIsaId)
      .then(r => setTags(r.data ?? []))
      .catch(e => notify.error(errText(e, 'Failed to load the tags.')))
      .finally(() => setTagsLoading(false));
  }, [mode, tagIsaId, notify]);
  const shownTags = useMemo(() => applySearch(tags, tagSearch, TAG_SEARCH_FIELDS), [tags, tagSearch]);

  // Size or Series: the user's pick, else Size when a picked tag carries a usable size.
  const sBasis = useMemo(() => s.basis || defaultBasis(tags.filter(t => picked.has(t.id))), [s.basis, tags, picked]);

  const togglePick = id => { setPicked(p => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; }); setProposal(null); };
  const pickShown = on => { setPicked(p => { const n = new Set(p); shownTags.forEach(t => (on ? n.add(t.id) : n.delete(t.id))); return n; }); setProposal(null); };

  const propose = useCallback(async () => {
    if (!sIsa || !s.manufacturerId || picked.size === 0) return;
    setBusy(true);
    try {
      const { data } = await dsSpecApi.propose({
        isa: sIsa.isaType, manufacturerId: Number(s.manufacturerId), tagIds: [...picked],
        basis: sBasis, seriesGroup: s.seriesGroup || undefined, unsizedGroup: s.unsizedGroup || undefined,
        suffix: s.suffix || undefined,
      });
      setProposal({
        ...data,
        rows: data.proposals.map(p => ({ include: !!p.code, description: proposalDescription(p, sIsa.description) })),
      });
    } catch (e) { notify.error(errText(e, 'Failed to group the tags.')); }
    finally { setBusy(false); }
  }, [sIsa, s, sBasis, picked, notify]);

  // ─── Create ──────────────────────────────────────────────────────────────
  const create = async (rows, after) => {
    setBusy(true);
    try {
      const { data } = await dsSpecApi.createIndexSheets(rows, auditCfg);
      const sum = createSummary(data);
      if (sum.success) notify.success(sum.success);
      if (sum.failure) notify.warning(sum.failure);
      after?.(data);
      onCreated?.();
    } catch (e) {
      notify.error(errText(e, 'Create failed.'));
      after?.(e?.response?.data ?? null);
    } finally { setBusy(false); }
  };

  const createGuided = () => create([{
    guided: true, code: gBuilt.code, manufacturerId: Number(g.manufacturerId),
    description: gFields.description, model: gFields.model, specification: gFields.specification,
    type: gFields.type || defaultType(gIsa?.isaType), status: gFields.status,
  }], () => { refreshNext(); setGSource(null); });

  const createManual = () => create([{
    guided: false, code: mCode.trim(), manufacturerId: Number(mMfrId),
    description: mFields.description, model: mFields.model, specification: mFields.specification,
    type: mFields.type || defaultType(mCheck.parts?.isa), status: mFields.status,
  }], data => { if (data?.created) setMCode(''); });

  const createSuggested = () => {
    const rows = proposal.proposals
      .map((p, i) => ({ p, r: proposal.rows[i] }))
      .filter(({ p, r }) => r.include && p.code)
      .map(({ p, r }) => ({
        guided: true, code: p.code, manufacturerId: Number(s.manufacturerId),
        description: r.description, model: sFields.model, specification: sFields.specification,
        type: sFields.type || defaultType(sIsa?.isaType), status: sFields.status,
        tagIds: p.tags.map(t => t.id), assignTags,
      }));
    if (rows.length === 0) return;
    create(rows, () => {
      setProposal(null);
      if (assignTags) dsSpecApi.getTags(tagIsaId).then(r => setTags(r.data ?? [])).catch(() => {});
    });
  };

  const setProposalRow = (i, k, v) => setProposal(p => ({ ...p, rows: p.rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const unsizedRow = proposal?.basis === 'Size' && proposal.proposals.some(p => p.group === null && p.tags.length > 0);

  // ─── Render ──────────────────────────────────────────────────────────────
  const sec = (id, label, body) => (
    <React.Fragment key={id}>
      <div className="stui-col-24">
        <CollapsibleSectionHeader label={label} collapsed={!!collapsed[id]} onToggle={() => toggleSection(id)} />
      </div>
      {!collapsed[id] && <div className="stui-col-24">{body}</div>}
    </React.Fragment>
  );

  return (
    <div className="stui-tab-body">
      <div className="stui-detail-grid">
        {mode === 'guided' && (
          <>
            {sec('g-find', 'Start From an Existing Sheet', (
              <>
                <div className="stui-row-8 stui-mb-md">
                  <div className="stui-search-wrapper">
                    <SearchClearIcon size={14} searchTerm={findQ} onClear={() => setFindQ('')} />
                    <input className="stui-search-input" value={findQ} onChange={e => setFindQ(e.target.value)}
                      placeholder="Start from a model number or specification..." />
                  </div>
                  {found.loading && <span className="stui-hint">Searching…</span>}
                </div>
                {found.error && <div className="stui-inline-notice stui-inline-notice-warning stui-mb-md">{found.error}</div>}
                {found.q && !found.loading && !found.error && (
                  found.rows.length === 0
                    ? <p className="stui-hint">{`No Index Sheet in the catalog or this database matches "${found.q}".`}</p>
                    : (
                      <div className="stui-data-grid-container stui-scroll-cap stui-mb-md">
                        <table className="stui-data-grid stui-data-grid-auto">
                          <thead><tr><th>Code</th><th>Model</th><th>Specification</th><th>Manufacturer</th><th>Status</th><th>Where</th><th /></tr></thead>
                          <tbody>
                            {found.rows.map(r => (
                              <tr key={`${r.source}|${r.indexSheet}`}>
                                <td className="stui-mono">{r.indexSheet}</td>
                                <td>{r.model}</td>
                                <td>{r.specification}</td>
                                <td>{r.manufacturer ?? r.manufacturerCode}</td>
                                <td>{r.status}</td>
                                <td>{r.source}</td>
                                <td>
                                  <button className="stui-btn stui-btn-secondary stui-btn-sm" disabled={!r.conforming} onClick={() => startFrom(r)}
                                    title={r.conforming ? 'Fill the form from this sheet' : 'This code does not follow the grammar'}>Use</button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )
                )}
              </>
            ))}
            {sec('g-code', 'Index Sheet Code', (
              <>
                {gSource && (
                  <div className={`stui-inline-notice${gSource.missing.length ? ' stui-inline-notice-warning' : ''} stui-mb-md`}>
                    {`Started from ${gSource.origin === 'This database' ? "this database's" : 'catalog'} sheet ${gSource.source}.` +
                      (gSource.missing.length ? ` This database has no ${gSource.missing.join(' and no ')}; pick them here.` : '')}
                  </div>
                )}
                <div className="stui-detail-grid">
                  <div className="stui-col-8"><IsaSelect id="isb-g-isa" isaTypes={isaTypes} value={g.isaTypeId} onChange={v => setGuided('isaTypeId', v)} /></div>
                  <div className="stui-col-8"><MfrSelect id="isb-g-mfr" manufacturers={manufacturers} value={g.manufacturerId} onChange={v => setGuided('manufacturerId', v)} /></div>
                  <div className="stui-col-4"><SuffixSelect id="isb-g-sfx" value={g.suffix} onChange={v => setGuided('suffix', v)} /></div>
                  <div className="stui-col-8 stui-field-group">
                    <label>Group by</label>
                    <div className="stui-segmented">
                      {['Size', 'Series'].map(b => (
                        <button key={b} className={`stui-segmented-btn${g.basis === b ? ' selected' : ''}`} onClick={() => setGuided('basis', b)}>{b}</button>
                      ))}
                    </div>
                  </div>
                  {g.basis === 'Size' && (
                    <div className="stui-col-8 stui-field-group">
                      <label htmlFor="isb-g-size">Size</label>
                      <StuiSelect id="isb-g-size" className="stui-field-input" value={g.sizeId} onChange={e => setGuided('sizeId', e.target.value)}>
                        <option value="">Pick a size…</option>
                        {sortedSizes.map(z => <option key={z.id} value={z.id}>{`${sizeLabel(z.size)}${z.group ? ` → ${z.group}` : ' (enter the group)'}`}</option>)}
                      </StuiSelect>
                    </div>
                  )}
                  {(g.basis === 'Series' || (gSize && !gSize.group)) && (
                    <div className="stui-col-8 stui-field-group">
                      <label htmlFor="isb-g-group">{g.basis === 'Series' ? 'Series (###)' : 'Group (###)'}</label>
                      <input id="isb-g-group" className="stui-field-input stui-mono" maxLength={3} value={g.manualGroup}
                        onChange={e => setGuided('manualGroup', e.target.value)} placeholder="e.g. 012" />
                    </div>
                  )}
                </div>
                <CodePreview
                  code={gBuilt.code}
                  tone={gBuilt.code ? 'success' : null}
                  note={gBuilt.code
                    ? 'next free instance across the catalog and every project.' +
                      (gNext.unreadable ? ` ${gNext.unreadable} database(s) could not be read; creating will be refused until they can.` : '')
                    : (gPrefix && !gNext.loading && !gNext.next ? 'Could not find the next free instance.' : gBuilt.error)} />
              </>
            ))}
            {sec('g-details', 'Sheet Details', (
              <SheetFields fields={gFields} setFields={setGFields} isa={gIsa?.isaType} idPrefix="isb-g" />
            ))}
            <div className="stui-col-24">
              <div className="stui-row-8">
                <button className="stui-btn stui-btn-primary" disabled={busy || !gBuilt.code} onClick={createGuided}>
                  <ListPlus size={14} /> {busy ? 'Creating…' : 'Create Index Sheet'}
                </button>
                <button className="stui-btn stui-btn-secondary" disabled={!gPrefix} onClick={refreshNext} title="Read the next free instance again"><RefreshCw size={14} /></button>
              </div>
            </div>
          </>
        )}

        {mode === 'manual' && (
          <>
            {sec('m-code', 'Index Sheet Code', (
              <>
                <div className="stui-detail-grid">
                  <div className="stui-col-8 stui-field-group">
                    <label htmlFor="isb-m-code">Index Sheet code</label>
                    <input id="isb-m-code" className="stui-field-input stui-mono" maxLength={40} value={mCode}
                      onChange={e => setMCode(e.target.value)} placeholder="CV-BEL-012-001-FL" />
                  </div>
                  <div className="stui-col-8 stui-field-group">
                    <label htmlFor="isb-m-mfr">Manufacturer</label>
                    <StuiSelect id="isb-m-mfr" className="stui-field-input" value={mMfrId} onChange={e => setMMfrId(e.target.value)} disabled={mMatches.length < 2}>
                      {mMatches.length !== 1 && <option value="">{mMatches.length === 0 ? (mCheck.ok ? `No manufacturer has code ${mCheck.parts.mfr}` : '—') : 'Pick one…'}</option>}
                      {mMatches.map(m => <option key={m.id} value={m.id}>{manufacturerLabel(m)}</option>)}
                    </StuiSelect>
                  </div>
                </div>
                <CodePreview
                  code={mCheck.ok ? mCode.trim().toUpperCase() : null}
                  tone={!mCode.trim() ? null : (!mCheck.ok || mInUse) ? 'warning' : 'success'}
                  note={!mCheck.ok ? mCheck.message
                    : mInUse ? 'is already in use.'
                      : mNext.loading ? 'checking…' : `is free. Next free instance on ${mCheck.parts.prefix}: ${mNext.next ?? '—'}.`} />
              </>
            ))}
            {sec('m-details', 'Sheet Details', (
              <SheetFields fields={mFields} setFields={setMFields} isa={mCheck.parts?.isa} idPrefix="isb-m" />
            ))}
            <div className="stui-col-24">
              <button className="stui-btn stui-btn-primary" disabled={busy || !mCheck.ok || mInUse || !mMfrId} onClick={createManual}>
                <ListPlus size={14} /> {busy ? 'Creating…' : 'Create Index Sheet'}
              </button>
            </div>
          </>
        )}

        {mode === 'suggested' && (
          <>
            {sec('s-group', 'Grouping', (
              <>
                <div className="stui-detail-grid">
                  <div className="stui-col-8"><IsaSelect id="isb-s-isa" label="ISA type of the codes" isaTypes={isaTypes} value={s.isaTypeId} onChange={v => setSuggested('isaTypeId', v)} /></div>
                  <div className="stui-col-8"><MfrSelect id="isb-s-mfr" manufacturers={manufacturers} value={s.manufacturerId} onChange={v => setSuggested('manufacturerId', v)} /></div>
                  <div className="stui-col-4"><SuffixSelect id="isb-s-sfx" value={s.suffix} onChange={v => setSuggested('suffix', v)} /></div>
                  <div className="stui-col-8">
                    <IsaSelect id="isb-s-tagisa" label="Tags of ISA type" isaTypes={isaTypes} value={tagIsaId} onChange={v => setSuggested('tagIsaTypeId', v)} />
                  </div>
                  <div className="stui-col-8 stui-field-group">
                    <label>Group by</label>
                    <div className="stui-segmented">
                      {['Size', 'Series'].map(b => (
                        <button key={b} className={`stui-segmented-btn${sBasis === b ? ' selected' : ''}`} onClick={() => setSuggested('basis', b)}>{b}</button>
                      ))}
                    </div>
                  </div>
                  {sBasis === 'Series' && (
                    <div className="stui-col-4 stui-field-group">
                      <label htmlFor="isb-s-series">Series (###)</label>
                      <input id="isb-s-series" className="stui-field-input stui-mono" maxLength={3} value={s.seriesGroup} onChange={e => setSuggested('seriesGroup', e.target.value)} />
                    </div>
                  )}
                  {unsizedRow && (
                    <div className="stui-col-4 stui-field-group">
                      <label htmlFor="isb-s-unsized">Group for unsized tags</label>
                      <input id="isb-s-unsized" className="stui-field-input stui-mono" maxLength={3} value={s.unsizedGroup}
                        onChange={e => setS(x => ({ ...x, unsizedGroup: e.target.value }))} placeholder="###" />
                    </div>
                  )}
                </div>
                {!s.basis && <p className="stui-hint">Grouping follows the picked tags: Size when one carries a valve size, otherwise Series. Click either to choose.</p>}
              </>
            ))}
            {sec('s-tags', `Tags (${picked.size} of ${tags.length} picked)`, (
              <>
                <div className="stui-page-header">
                  <div className="stui-row-8">
                    <span className="stui-text-sm-muted">
                      {tagsLoading ? 'Loading tags…' : `${picked.size} of ${tags.length} tags picked; tags without an Index Sheet are listed first`}
                    </span>
                    <button className="stui-btn stui-btn-secondary stui-btn-sm" disabled={shownTags.length === 0} onClick={() => pickShown(true)}>Pick shown</button>
                    <button className="stui-btn stui-btn-secondary stui-btn-sm" disabled={picked.size === 0} onClick={() => pickShown(false)}>Clear shown</button>
                  </div>
                  <div className="stui-row-8">
                    <div className="stui-search-wrapper">
                      <SearchClearIcon size={14} searchTerm={tagSearch} onClear={() => setTagSearch('')} />
                      <input className="stui-search-input" value={tagSearch} onChange={e => setTagSearch(e.target.value)} placeholder="Search tags..." title={SEARCH_HINT} />
                    </div>
                    <button className="stui-btn stui-btn-primary" disabled={busy || !sIsa || !s.manufacturerId || picked.size === 0 || (sBasis === 'Series' && !normalizeGroup(s.seriesGroup))} onClick={propose}>
                      <Wand2 size={14} /> Propose
                    </button>
                  </div>
                </div>
                <div className="stui-data-grid-container stui-scroll-cap stui-mt-sm stui-mb-md">
                  <table className="stui-data-grid stui-data-grid-auto">
                    <thead><tr><th /><th>Tag</th><th>Description</th><th>Size</th><th>Group</th><th>Index Sheet</th></tr></thead>
                    <tbody>
                      {shownTags.length === 0 && (
                        <tr><td colSpan={6} className="stui-td-empty">{tagIsaId ? (tagsLoading ? 'Loading…' : 'No tags.') : 'Pick an ISA type.'}</td></tr>
                      )}
                      {shownTags.map(t => (
                        <tr key={t.id}>
                          <td><input type="checkbox" className="stui-checkbox-input" checked={picked.has(t.id)} onChange={() => togglePick(t.id)} aria-label={`Pick ${t.tagId}`} /></td>
                          <td className="stui-mono">{t.tagId}</td>
                          <td>{t.description}</td>
                          <td>{sizeLabel(t.size)}</td>
                          <td className="stui-mono">{t.group ?? ''}</td>
                          <td className={t.hasSheet ? 'stui-mono' : 'stui-td-muted'}>{t.indexSheet ?? ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ))}
            {proposal && sec('s-proposal', `Proposed Index Sheets (${proposal.basis})`, (
              <>
                {proposal.unreadable > 0 && (
                  <div className="stui-inline-notice stui-inline-notice-warning stui-mb-md">
                    {`${proposal.unreadable} database(s) could not be read; creating will be refused until they can.`}
                  </div>
                )}
                <div className="stui-data-grid-container stui-mb-md">
                  <table className="stui-data-grid stui-data-grid-auto">
                    <thead><tr><th /><th>Code</th><th>Sizes</th><th>Tags</th><th>Description</th></tr></thead>
                    <tbody>
                      {proposal.proposals.map((p, i) => (
                        <tr key={p.group ?? `none-${i}`}>
                          <td><input type="checkbox" className="stui-checkbox-input" disabled={!p.code} checked={proposal.rows[i].include && !!p.code}
                            onChange={e => setProposalRow(i, 'include', e.target.checked)} aria-label={`Create ${p.code ?? 'this sheet'}`} /></td>
                          <td className={p.code ? 'stui-mono' : 'stui-td-muted'}>{p.code ?? 'Enter a group for unsized tags, then Propose again'}</td>
                          <td>{p.sizes.map(sizeLabel).join(', ')}</td>
                          <td title={p.tags.map(t => t.tagId).join(', ')}>{p.tags.length}</td>
                          <td>
                            <input className="stui-field-input" maxLength={220} value={proposal.rows[i].description}
                              onChange={e => setProposalRow(i, 'description', e.target.value)} aria-label="Description" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ))}
            {proposal && sec('s-details', 'Sheet Details', (
              <>
                <SheetFields fields={sFields} setFields={setSFields} isa={sIsa?.isaType} idPrefix="isb-s" hideDescription />
                <label className="stui-checkbox stui-mb-md">
                  <input type="checkbox" className="stui-checkbox-input" checked={assignTags} onChange={e => setAssignTags(e.target.checked)} />
                  Assign the picked tags to their new Index Sheet (replaces each tag's current Index Sheet)
                </label>
                <button className="stui-btn stui-btn-primary" disabled={busy || !proposal.rows.some((r, i) => r.include && proposal.proposals[i].code)} onClick={createSuggested}>
                  <ListPlus size={14} /> {busy ? 'Creating…' : `Create ${proposal.rows.filter((r, i) => r.include && proposal.proposals[i].code).length} Index Sheet(s)`}
                </button>
              </>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
